/**
 * Appfolio Property Transformer
 * Transforms Appfolio property CSV data to our property models
 */

import type {
  PropertyTransformer,
  PropertyRecord,
  CSVRow,
  AppfolioPropertyRow,
  PropertyUploadContext,
  ValidationResult,
} from '@/types/csv-upload'
import { PropertyType, ListingType } from '@super/shared-types'
import { buildPropertyComposite } from './appfolio-utils'

// Required fields for Appfolio property CSV
// Property ID and Property Type columns are absent in AppFolio exports that omit numeric IDs.
// externalId falls back to a name+address composite; propertyType is optional.
export const APPFOLIO_PROPERTY_REQUIRED_FIELDS = [
  'Property Name',
  'Property Street Address 1',
  'Property City',
  'Property State',
  'Property Zip',
] as const

// Property Type mapping from CSV values to enum
const PROPERTY_TYPE_MAPPING: Record<string, PropertyType> = {
  'single family': PropertyType.SINGLE_FAMILY,
  'single-family': PropertyType.SINGLE_FAMILY,
  single_family: PropertyType.SINGLE_FAMILY,
  sf: PropertyType.SINGLE_FAMILY,
  'multi family': PropertyType.MULTI_FAMILY,
  'multi-family': PropertyType.MULTI_FAMILY,
  multi_family: PropertyType.MULTI_FAMILY,
  mf: PropertyType.MULTI_FAMILY,
  commercial: PropertyType.COMMERCIAL,
  comm: PropertyType.COMMERCIAL,
  'mixed-use': PropertyType.MIXED_USE,
  'mixed use': PropertyType.MIXED_USE,
  mixed_use: PropertyType.MIXED_USE,
}

// Listing Type mapping from CSV values to enum
const LISTING_TYPE_MAPPING: Record<string, ListingType> = {
  apartment: ListingType.APARTMENT,
  apt: ListingType.APARTMENT,
  condo: ListingType.CONDO,
  condominium: ListingType.CONDO,
  house: ListingType.HOUSE,
  home: ListingType.HOUSE,
  townhouse: ListingType.TOWNHOUSE,
  townhome: ListingType.TOWNHOUSE,
  th: ListingType.TOWNHOUSE,
}

export const appfolioPropertyTransformer: PropertyTransformer = {
  providerName: 'appfolio',

  transformProperty(
    row: CSVRow,
    context: PropertyUploadContext,
  ): PropertyRecord | null {
    const appfolioRow = row as AppfolioPropertyRow

    // externalId: numeric Property ID when present, otherwise a synthetic name+address composite
    const externalId = appfolioRow['Property ID']?.trim() || undefined
    const addressStreet1 = appfolioRow['Property Street Address 1']?.trim()
    const city = appfolioRow['Property City']?.trim()
    const state = appfolioRow['Property State']?.trim()
    const zip = appfolioRow['Property Zip']?.trim()
    const propertyNameRaw = appfolioRow['Property Name']?.trim()
    // Fall back to street address if property name is missing
    const propertyName = propertyNameRaw || addressStreet1
    const propertyTypeRaw = appfolioRow['Property Type']?.trim()?.toLowerCase()
    const listingTypeRaw = appfolioRow['Listing Type']?.trim()?.toLowerCase()

    const missingFields: string[] = []
    if (!addressStreet1) missingFields.push('Property Street Address 1')
    if (!city) missingFields.push('Property City')
    if (!state) missingFields.push('Property State')
    if (!zip) missingFields.push('Property Zip')
    if (!propertyName)
      missingFields.push('Property Name or Property Street Address 1')

    if (missingFields.length > 0) {
      context.skipReason = `Missing required fields: ${missingFields.join(', ')}`
      return null
    }

    // Property type is optional — AppFolio exports may omit the column
    const propertyType = propertyTypeRaw
      ? PROPERTY_TYPE_MAPPING[propertyTypeRaw]
      : undefined
    if (propertyTypeRaw && !propertyType) {
      context.skipReason = `Invalid property type '${appfolioRow['Property Type']}'. Valid values: ${Object.keys(PROPERTY_TYPE_MAPPING).join(', ')}`
      return null
    }

    // Listing type is optional
    const listingType = listingTypeRaw
      ? LISTING_TYPE_MAPPING[listingTypeRaw]
      : undefined
    if (listingTypeRaw && !listingType) {
      context.skipReason = `Invalid listing type '${appfolioRow['Listing Type']}'. Valid values: ${Object.keys(LISTING_TYPE_MAPPING).join(', ')}`
      return null
    }

    // Build address object
    const propertyAddress = {
      addressStreet1: addressStreet1 || '',
      addressStreet2:
        appfolioRow['Property Street Address 2']?.trim() || undefined,
      addressCity: city || '',
      addressState: state || '',
      addressPostcode: zip || '',
      addressCountry: 'US', // Default for Appfolio
      addressLat: null,
      addressLng: null,
    }

    return {
      name: propertyName || '',
      propertyAddress,
      unit: appfolioRow.Property?.trim() || undefined, // Unit info if available
      externalId:
        externalId ||
        buildPropertyComposite(
          propertyName || '',
          addressStreet1 || '',
          city || '',
          state || '',
          zip || '',
        ),
      portfolioId: undefined, // Not available in this CSV format
      propertyType,
      listingType,
      tags: undefined,
    }
  },

  validateProperty(
    record: PropertyRecord,
    _context: PropertyUploadContext,
  ): ValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!record.name) {
      errors.push(
        'Property Name is required - check the "Property Name" column',
      )
    }

    if (!record.propertyAddress.addressStreet1) {
      errors.push(
        'Property Street Address 1 is required - check the "Property Street Address 1" column',
      )
    }
    if (!record.propertyAddress.addressCity) {
      errors.push(
        'Property City is required - check the "Property City" column',
      )
    }
    if (!record.propertyAddress.addressState) {
      errors.push(
        'Property State is required - check the "Property State" column',
      )
    }
    if (!record.propertyAddress.addressPostcode) {
      errors.push('Property Zip is required - check the "Property Zip" column')
    }

    // Property type is optional in the new export format — no validation error

    // Postal code format validation (basic US zip code)
    if (
      record.propertyAddress.addressPostcode &&
      !/^\d{5}(-\d{4})?$/.test(record.propertyAddress.addressPostcode)
    ) {
      warnings.push(
        'Property Zip code format may be invalid - expected format: 12345 or 12345-6789',
      )
    }

    // State code validation (should be 2 characters)
    if (
      record.propertyAddress.addressState &&
      record.propertyAddress.addressState.length !== 2
    ) {
      warnings.push(
        'Property State should be a 2-character state code (e.g., CA, NY, TX)',
      )
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    }
  },
}
